/**
 * L'atelier de forgemagie (GDD §19).
 *
 * Une pièce, ses lignes, et pour chacune : où elle en est dans sa fourchette,
 * ce que coûte la prochaine tentative et quelles chances on a. Tout est affiché
 * **avant** de payer — une tentative doit être un pari assumé, jamais une
 * surprise.
 *
 * Les lots ×10 et « jusqu'en haut » ne sont pas un confort : perfectionner une
 * pièce de niveau 200 demande cent soixante-dix tentatives.
 */
import type { Ctx } from "../contexte.ts"
import { item, type Contenu } from "../../sim/contenu.ts"
import { trouverPorte, LOT_FORGE_MAX } from "../../sim/simulate.ts"
import {
  lignes, coutForge, chanceForge, auMaximum, auPlafond, auPlancher, puits, coutMalediction, abandonne,
  type Ligne,
} from "../../sim/forge.ts"
import { perfection, effetLisible, type Porte } from "../../sim/objets.ts"
import { esc, nb, jauge, vert, orJauge, rougeJauge } from "../fmt.ts"
import { icone, ic } from "../sprites.ts"

/** Le nom lisible d'une ligne, tel que l'infobulle de l'objet l'affiche. */
function nomLigne(c: Ctx, itemId: number, effet: number): string {
  const it = item(c.contenu, itemId)
  const e = it?.effets.find((x) => x.effet === effet)
  return e && effetLisible(e.stat) ? e.stat : "—"
}

/** Ce que le dernier lot a produit, résumé en une phrase. */
export function resumeForge(issues: { type: string; surpuissance?: boolean }[]): string {
  if (issues.length === 0) return ""
  if (issues[0]?.type === "maudite") return "Ligne maudite : une marge de surpuissance regagnée."
  const r = issues.filter((i) => i.type === "reussite").length
  const sur = issues.filter((i) => i.type === "reussite" && i.surpuissance).length
  const e = issues.filter((i) => i.type === "echec").length
  const stop = issues.find((i) => i.type === "impossible") as { raison?: string } | undefined
  const fin = stop?.raison === "kamas" ? " Les kamas ont manqué."
    : stop?.raison === "maximum" ? " La ligne est au maximum."
    : ""
  if (r + e === 0) return fin.trim()
  const gain = sur > 0 ? ` Dont ${sur} au-dessus du maximum.` : ""
  return `${r} réussite${r > 1 ? "s" : ""}, ${e} échec${e > 1 ? "s" : ""}.${gain}${fin}`
}

/**
 * Y a-t-il quelque chose à forger sur cette pièce ?
 *
 * Neuf cent quatre-vingts recettes n'ont aucun effet variable : proposer la
 * forgemagie dessus mènerait à un atelier vide.
 */
export function forgeable(contenu: Contenu, porte: Porte): boolean {
  const it = item(contenu, porte.item)
  return Boolean(it && lignes(it, porte).length > 0)
}

export function vueForge(c: Ctx): string {
  const { etat, contenu } = c
  if (!c.forgeUid) return ""
  const ou = trouverPorte(etat, c.forgeUid)
  const it = ou ? item(contenu, ou.porte.item) : null
  if (!ou || !it) return ""

  const l = lignes(it, ou.porte)
  const pct = perfection(it, ou.porte)
  const marge = puits(it, ou.porte)
  const porteur = ou.ou === "equipe"
    ? etat.personnages.find((p) => p.id === ou.perso)?.nom ?? null
    : null

  return `
  <section class="carte forge">
    <div class="entete">
      <span class="ic" style="${icone("items", it.id, 48)}"></span>
      <div class="ident">
        <b>${esc(it.nom)}</b>
        <span>niv ${it.niveau} · <b class="pct">${pct} % parfait</b>${
          porteur ? ` · porté par ${esc(porteur)}` : " · au coffre"}</span>
      </div>
      <span class="puits ${marge > 0 ? "ok" : "vide"}" title="Chaque cran au-dessus d'un maximum consomme une marge ; chaque cran maudit en rend une">
        Puits <b>${marge}</b>
      </span>
      <button class="mini" id="fermerForge">Fermer</button>
    </div>

    ${l.length === 0
      ? `<p class="aide">Cette pièce n'a aucune ligne à fourchette : son jet est figé, il n'y a rien à forger.</p>`
      : `<p class="aide">Pousser une ligne peut faire chuter une autre. Une ligne déjà au maximum ne chute jamais — c'est ce qui rend la perfection atteignable, ligne après ligne. Au-delà du maximum, la <b>surpuissance</b> : chaque cran coûte deux fois et demie le précédent, ne réussit qu'une fois sur sept, mais ce qui est acquis ne redescend jamais sous le maximum naturel.</p>
    <p class="aide">Chaque cran de surpuissance consomme une <b>marge</b>. Quand il n'y en a plus, il faut en gagner : <b>maudire</b> une ligne la fait descendre sous son minimum et rend une marge. C'est là toute la décision — une pièce monstrueuse quelque part est forcément mauvaise ailleurs.</p>
    <div class="lignesForge">${l.map((x) => ligneForge(c, it.id, x, ou.porte)).join("")}</div>`}

    ${c.forgeIssues.length > 0
      ? `<p class="verdict ${c.forgeIssues.some((i) => i.type === "reussite") ? "ok" : "ko"}">${esc(resumeForge(c.forgeIssues))}</p>`
      : ""}
  </section>`
}

function ligneForge(c: Ctx, itemId: number, x: Ligne, porte: Porte): string {
  const it = item(c.contenu, itemId)!
  const coutMal = coutMalediction(it)
  // On ne propose de maudire que s'il y a quelque chose à financer : sur une
  // pièce qu'on n'a pas encore finie, abîmer une ligne n'aurait aucun sens.
  const peutMaudire = lignes(it, porte).some((y) => auMaximum(y))
  const fini = auPlafond(x)
  const sur = x.surpuissance > 0
  const maudite = x.malediction > 0
  const marge = puits(it, porte)
  const bloque = auMaximum(x) && marge <= 0
  const cout = coutForge(it, x)
  const chance = Math.round(chanceForge(x) * 100)
  const assez = c.etat.kamas >= cout
  const nom = nomLigne(c, itemId, x.effet)

  return `<div class="ligneForge ${fini ? "finie" : ""} ${sur ? "surpuissante" : ""} ${maudite ? "maudite" : ""}">
    <div class="tete">
      <span class="n">${esc(nom)}</span>
      <span class="v">${nb(x.valeur)} <em>/ ${nb(x.max)}</em>${
        sur ? ` <b class="sur">+${nb(x.surpuissance)}</b>` : ""}${
        maudite ? ` <b class="mal">−${nb(x.malediction)}</b>` : ""}</span>
    </div>
    ${jauge(x.position, sur ? orJauge : x.position >= 1 ? vert : x.position > .6 ? orJauge : rougeJauge, `f-${x.effet}`)}
    ${fini
      ? `<p class="fini">Au plafond absolu. Il n'y a plus rien à en tirer.</p>`
      : `<div class="mise">
          <span class="prix ${assez ? "" : "ko"}">${nb(cout)} ${ic("kamas", 14)}</span>
          <span class="chance">${chance} % de réussite</span>
          <span class="pas">+${nb(x.pas)} si ça passe</span>
        </div>
        ${auMaximum(x)
          ? `<p class="fini">${sur
              ? `Surpuissance : un échec te fait redescendre d'un cran, jamais sous ${nb(x.max)}.`
              : `Au maximum naturel. Au-delà, chaque cran coûte deux fois et demie le précédent — et ne peut plus jamais retomber sous ${nb(x.max)}.`}</p>`
          : ""}
        ${bloque ? `<p class="fini ko">Plus de marge : maudis une autre ligne pour en regagner une.</p>` : ""}
        <div class="lots">
          <button class="mini ${assez && !bloque ? "primaire" : ""}" data-forger="${x.effet}" data-lot="1"
            ${assez && !bloque ? "" : "disabled"}>${auMaximum(x) ? "Surpuissance" : "Tenter"}</button>
          <button class="mini" data-forger="${x.effet}" data-lot="10" ${assez && !bloque ? "" : "disabled"}>×10</button>
          ${auMaximum(x) ? "" : `<button class="mini" data-forger="${x.effet}" data-lot="${LOT_FORGE_MAX}" ${assez ? "" : "disabled"}>Jusqu'en haut</button>`}
        </div>`}
    ${auPlancher(x) || !peutMaudire
      ? ""
      : `<button class="mini maudire" data-maudire="${x.effet}" ${c.etat.kamas >= coutMal ? "" : "disabled"}
          title="${abandonne(x)
            ? `Renonce à cette ligne : elle tombe à ${nb(x.min)}`
            : "Creuse sous le minimum et rend une marge de surpuissance"}"
          >${abandonne(x)
            ? `Renoncer · tombe à ${nb(x.min)}`
            : `Maudire · −${nb(x.pas)}, +1 marge`} pour ${nb(coutMal)} ${ic("kamas", 14)}</button>`}
  </div>`
}
